import { memo } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { LOG_COLUMNS, getVal } from "@/lib/logColumns";
import { fmtDate, fmtDateTime, fmtINR, fmtPct } from "@/lib/format";
import { LeakBadges } from "./LeakBadges";

const BOOL_LABEL = new Map([[true, "Yes"], [false, "No"]]);

const sortIcon = (sort, key) => {
  if (sort.key !== key) return ArrowUpDown;
  return sort.dir === "asc" ? ArrowUp : ArrowDown;
};

const cell = (col, row) => {
  const v = getVal(col, row);
  switch (col.type) {
    case "money": return fmtINR(v);
    case "pct": return fmtPct(v);
    case "date": return fmtDate(v);
    case "datetime": return fmtDateTime(v);
    case "bool": return BOOL_LABEL.get(v) ?? "—";
    case "flags": return <LeakBadges flags={row._flags} prefix={`log-${row.id}`} />;
    default: return v || "—";
  }
};

const Row = memo(function Row({ row, onOpen }) {
  return (
    <tr data-testid={`lead-log-row-${row.id}`} onClick={() => onOpen(row.id)} className="log-row">
      {LOG_COLUMNS.map((c, i) => (
        <td key={c.key} data-testid={i === 0 ? `lead-log-cell-name-${row.id}` : undefined}
          className={`${i === 0 ? "log-first font-semibold text-slate-50" : ""} ${["money", "pct"].includes(c.type) ? "text-right font-mono" : ""}`}>
          {i === 0 && row.is_sample ? <span className="flex items-center gap-1.5">{row.name}<span className="sample-chip">Sample</span></span> : cell(c, row)}
        </td>
      ))}
    </tr>
  );
});

export const LogTable = ({ rows, sort, onSort, onOpen }) => (
  <div className="log-scroll" data-testid="lead-log-table">
    <table className="log-table">
      <thead>
        <tr>
          {LOG_COLUMNS.map((c, i) => {
            const Icon = sortIcon(sort, c.key);
            return (
              <th key={c.key} className={i === 0 ? "log-first" : ""}>
                <button type="button" data-testid={`lead-log-sort-${c.key}`} onClick={() => onSort(c)} className="inline-flex items-center gap-1 whitespace-nowrap">
                  {c.label} <Icon className={`h-3 w-3 ${sort.key === c.key ? "text-indigo-400" : "text-slate-600"}`} />
                </button>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>{rows.map((r) => <Row key={r.id} row={r} onOpen={onOpen} />)}</tbody>
    </table>
  </div>
);
